'use client';

import {
  Button,
  HStack,
  StackProps,
  SimpleGrid,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { usePathname } from 'next/navigation';
import useNavbar from '~/hooks/useNavbar';
import { ROUTES } from '@habity/constants';
import { useUser } from '~/hooks/useUser';
import { useUIStore } from '~/store';
import { ICONS } from '~/constants/icons';
import { useTranslations } from 'next-intl';
import { HeaderMenu } from './Navigation';
import { UserAvatar } from './UserAvatar';
import { getSellPath } from '@habity/services';
import { Logo } from '../Logo';
import { useEffect, useState } from 'react';
import { LinkWithFilterReset } from '../Link';

interface DesktopHeaderProps extends StackProps {
  containerMaxWidth?: string;
}

export function DesktopHeader({
  containerMaxWidth,
  ...props
}: DesktopHeaderProps) {
  const { rootProps } = useNavbar();
  const t = useTranslations('Header');
  const { user, isUserLoaded } = useUser();
  const modals = useUIStore((state) => state.modals);
  const openLoginModal = useUIStore((state) => state.openLoginModal);
  const pathname = usePathname();

  // Load the links client-side
  // Chakra detects mobile breakpoint on client-side so it defaults to
  // desktop header on first-load, leading to a bad UX
  // By not loading the links until the client-side code kicks in,
  // we allow chakra time to detect mobile breakpoint and switch to mobile header
  // <10% of our users are on desktop so this has very minimal adverse effects
  const [showLinks, setShowLinks] = useState<boolean>(false);

  useEffect(() => {
    setShowLinks(true);
  }, [])

  return (
    <HStack
      as="nav"
      role="navigation"
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex="docked"
      h="70px"
      justifyContent="center"
      alignItems="center"
      {...rootProps}
      {...props}
    >
      {/* Container */}
      <HStack
        px={{ mobile: '4', desktop: '10' }}
        flex={1}
        h="100%"
        alignItems="center"
        maxW={containerMaxWidth}
      >
        {/* Content */}
        {/* Use a grid to align the left/right columns to match p0 banner as Marcus wanted */}
        {/* instead of center aligning the links as they were before */}
        <SimpleGrid
          templateColumns="1fr 2fr"
          alignItems="center"
          w="100%"
          flex={1}
          position="relative"
        >
          {/* Left: Logo */}
          <Logo />

          {/* Right: Links and Actions */}
          <HStack spacing={4} justifyContent="space-between">
            <HStack spacing="8" fontSize="sm" fontWeight="medium">
              {showLinks && (
                <>
                  <LinkWithFilterReset href={ROUTES.SEARCH.HOMES}>
                    Våra bostäder
                  </LinkWithFilterReset>
                  <LinkWithFilterReset href={ROUTES.SEARCH.BROKERS}>
                    Våra mäklare
                  </LinkWithFilterReset>
                  <LinkWithFilterReset href={ROUTES.SEARCH.MUNICIPALITIES}>
                    Våra områden
                  </LinkWithFilterReset>
                  <LinkWithFilterReset href={ROUTES.CMS.HOW_IT_WORKS}>
                    Vår tjänst
                  </LinkWithFilterReset>

                </>
              )}
            </HStack>

            <HStack>
              <Link href={getSellPath(undefined, 'valuation')}>
                <Button
                  variant="subtle"
                  leftIcon={<ICONS.SELL />}
                  px="6"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  Värdera
                </Button>
              </Link>
              {user ? (
                <HeaderMenu menuTrigger={<UserAvatar />} />
              ) : isUserLoaded ? (
                <Button
                  onClick={() => openLoginModal()}
                  variant="secondary"
                  isActive={pathname === ROUTES.LOGIN || modals.loginOpen}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  {t('Links.login')}
                </Button>
              ) : null}
            </HStack>
          </HStack>
        </SimpleGrid>
      </HStack>
    </HStack>
  );
}
